/* =====================================================================
   assets/soi-carousel.js · Spirit of India carousels · M0 (05 C37, §3.1, §7.4)
   <soi-carousel> wraps a CSS scroll-snap track ([data-soi-track]) and optional controls rendered by
   snippets/soi-carousel-controls.liquid ([data-soi-controls] with prev/next, dots, counter).
   Swipe, keyboard and pointer all work; no autoplay ever (05 D7). The focusable track is named after its
   section heading („Nichts versteckt. Nur reine Öle., Karussell"); ArrowLeft / ArrowRight move one slide,
   Home / End jump to the first / last (F-R2-A1-14). Reduced motion jumps instead of scrolling smoothly.
   Coverflow ([data-coverflow]) moves are measured from the centred slide, and data-start centres that slide
   from geometry (R2-G-05), so the opening card is the snap target from the first frame.
   Coverflow fallback: with data-coverflow, the centred slide gets .is-active when the browser lacks
   scroll-driven animations (animation-timeline: view()).
   Without JS the track still scrolls natively; controls stay hidden.
   ===================================================================== */
(() => {
  'use strict';
  if (customElements.get('soi-carousel')) return;
  const STR = window.soiStrings || {};
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class SoiCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-soi-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      if (!this.slides.length) return;
      this.controls = this.querySelector('[data-soi-controls]');
      this.prev = this.querySelector('[data-soi-prev]');
      this.next = this.querySelector('[data-soi-next]');
      this.dots = [...this.querySelectorAll('[data-soi-dot]')];
      this.counter = this.querySelector('[data-soi-counter-current]');
      this.index = 0;
      this.visible = new Set();

      this.setAttribute('aria-roledescription', STR.carousel || 'carousel');
      if (!this.hasAttribute('role')) this.setAttribute('role', 'region');
      const total = this.slides.length;
      this.slides.forEach((s, i) => {
        /* <li> slides keep their native listitem role (role=group on an li is invalid ARIA and breaks the list,
           axe aria-allowed-role / list); they only get the „Folie n von N" label. Other slides become groups. */
        if (s.tagName !== 'LI') {
          s.setAttribute('role', 'group');
          s.setAttribute('aria-roledescription', STR.slide || 'slide');
        }
        if (!s.hasAttribute('aria-label') && STR.slideOf) {
          s.setAttribute('aria-label', STR.slideOf.replace('[n]', i + 1).replace('[total]', total));
        }
      });
      if (!this.track.hasAttribute('tabindex')) this.track.tabIndex = 0;
      if (this.track.tabIndex >= 0 && !this.track.hasAttribute('aria-label') && !this.track.hasAttribute('aria-labelledby')) {
        const sec = this.closest('[data-soi-section]') || this.closest('section');
        const h = sec && sec.querySelector('h2, h1, h3');
        const name = h ? h.textContent.replace(/[¹‡*]/g, '').replace(/\s+/g, ' ').trim() : '';
        const kind = STR.carousel || 'Karussell';
        this.track.setAttribute('aria-label', name ? `${name}, ${kind}` : kind);
      }
      this.isCoverflow = this.hasAttribute('data-coverflow');

      /* controls only while the row overflows: a row whose cards all fit shows no inert arrows (re-checked on resize) */
      this.syncControls = () => {
        if (!this.controls) return;
        const overflows = this.track.scrollWidth > this.track.clientWidth + 1;
        this.controls.hidden = this.slides.length < 2 || !overflows;
      };
      this.syncControls();
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.syncControls());
        this.ro.observe(this.track);
      }
      this.prev && this.prev.addEventListener('click', () => this.go(this.firstVisible() - 1));
      this.next && this.next.addEventListener('click', () => this.go(this.lastVisible() + 1 >= total ? total - 1 : this.firstVisible() + 1));
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.soiDot))));
      this.track.addEventListener('keydown', (e) => {
        if (e.target !== this.track || e.altKey || e.ctrlKey || e.metaKey) return;
        const at = this.current();
        let to = null;
        if (e.key === 'ArrowRight') to = at + 1;
        else if (e.key === 'ArrowLeft') to = at - 1;
        else if (e.key === 'Home') to = 0;
        else if (e.key === 'End') to = total - 1;
        if (to === null) return;
        e.preventDefault();
        this.go(to);
      });

      this.coverflow = this.hasAttribute('data-coverflow') && !(window.CSS && CSS.supports('animation-timeline: view()'));
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            const i = this.slides.indexOf(en.target);
            if (en.intersectionRatio >= 0.6) this.visible.add(i); else this.visible.delete(i);
          });
          this.update();
        }, { root: this.track, threshold: [0, 0.6, 1] });
        this.slides.forEach((s) => this.io.observe(s));
      }
      /* data-start: open on that slide. Coverflow: centred from geometry (the track's padding already centres
         slide 0, so offsets must not subtract a second centre; R2-G-05). Index, active class and counter are set
         now, before the first IntersectionObserver callback, and kept until the user scrolls. */
      const start = parseInt(this.dataset.start || '', 10);
      if (start > 0 && start < total) {
        this.track.scrollLeft = this.targetLeft(start);
        this.index = start;
        this.pinned = start;
        const unpin = () => { this.pinned = null; };
        ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((ev) => this.track.addEventListener(ev, unpin, { passive: true, once: true }));
        this.addEventListener('click', (e) => { if (e.target.closest('[data-soi-prev], [data-soi-next], [data-soi-dot]')) unpin(); }, true);
      }
      this.update();
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); if (this.ro) this.ro.disconnect(); }
    firstVisible() { return this.visible.size ? Math.min(...this.visible) : this.index; }
    lastVisible() { return this.visible.size ? Math.max(...this.visible) : this.index; }
    /* the slide whose centre is nearest the track's centre (coverflow), else the first fully visible one */
    centred() {
      const tr = this.track.getBoundingClientRect();
      const mid = tr.left + this.track.clientWidth / 2;
      let best = this.index; let dist = Infinity;
      this.slides.forEach((s, i) => { const r = s.getBoundingClientRect(); const d = Math.abs(r.left + r.width / 2 - mid); if (d < dist) { dist = d; best = i; } });
      return best;
    }
    current() {
      if (this.pinned != null) return this.pinned;
      return this.isCoverflow ? this.centred() : this.firstVisible();
    }
    targetLeft(n) {
      const slide = this.slides[n];
      if (this.isCoverflow) {
        const tr = this.track.getBoundingClientRect();
        const r = slide.getBoundingClientRect();
        return Math.max(0, this.track.scrollLeft + (r.left + r.width / 2) - (tr.left + this.track.clientWidth / 2));
      }
      return slide.offsetLeft - this.slides[0].offsetLeft;
    }
    go(i) {
      const n = Math.max(0, Math.min(this.slides.length - 1, i));
      this.pinned = null;
      this.track.scrollTo({ left: this.targetLeft(n), behavior: reduce() ? 'auto' : 'smooth' });
      this.index = n;
    }
    update() {
      const first = this.firstVisible();
      const last = this.lastVisible();
      if (this.pinned != null) {
        /* opening state (data-start) holds until the user moves the row */
        const p = this.pinned;
        if (this.counter) this.counter.textContent = String(p + 1);
        if (this.prev) this.prev.disabled = p <= 0;
        if (this.next) this.next.disabled = p >= this.slides.length - 1;
        this.dots.forEach((d, i) => { if (i === p) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
        if (this.coverflow) this.slides.forEach((s, i) => s.classList.toggle('is-active', i === p));
        this.dispatchEvent(new CustomEvent('soi:carousel-change', { bubbles: true, detail: { index: p } }));
        return;
      }
      this.index = this.isCoverflow ? this.centred() : first;
      const total = this.slides.length;
      if (this.prev) this.prev.disabled = first <= 0;
      if (this.next) this.next.disabled = last >= total - 1;
      const current = this.visible.size > 1 && this.coverflow ? Math.round((first + last) / 2) : first;
      this.dots.forEach((d, i) => {
        if (i === first) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
      const shown = this.visible.size > 1 && this.hasAttribute('data-coverflow') ? Math.round((first + last) / 2) : first;
      if (this.counter) this.counter.textContent = String(shown + 1);
      if (this.coverflow) this.slides.forEach((s, i) => s.classList.toggle('is-active', i === current));
      this.dispatchEvent(new CustomEvent('soi:carousel-change', { bubbles: true, detail: { index: first } }));
    }
  }
  customElements.define('soi-carousel', SoiCarousel);
})();
